import React from 'react';
import DateFilter from 'reusable/datafilter';
import Export from 'reusable/export';
import {
  bundledDeviceTimeZoneExportFields,
  CsvCondition,
  csvDateTime,
  deviceTimeZoneExportFields,
} from 'config/csv';
import Search from 'reusable/search';
import SortBy from 'reusable/sortby';
import { useTranslation } from 'react-i18next';

interface ToolbarProps {
  searchValue: string;
  sortValue: string;
  dateValue: string;
  currentPage: number;
  onSearchChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onDateChange: (value: string) => void;
  onPageChange: (value: number) => void;
  DataForExport: any[];
  activetab: string;
}

const Toolbar: React.FC<ToolbarProps> = ({
  searchValue,
  sortValue,
  dateValue,
  currentPage,
  onSearchChange,
  onSortChange,
  onDateChange,
  onPageChange,
  DataForExport,
  activetab,
}) => {
  const { t } = useTranslation(['alcohol', 'common']);
  const sortingOptions = [
    { label: t('sort.default', { ns: 'common' }), value: '' },
    { label: t('sort.create-time', { ns: 'common' }), value: 'create_time' },
  ];
  const handleSortChange = (value: string) => {
    onSortChange(value);
    onPageChange(1);
  };

  const handleSearchChange = (value: string) => {
    onSearchChange(value);
    onPageChange(1);
  };

  const handleDateChange = (value: string) => {
    onDateChange(value);
    onPageChange(1);
  };

  const exportFields = [
    // ADM-11-AC1。行を画面・API の記録へたどれるよう、測定記録の ID を先頭に出す。
    { header: 'Test ID', field: 'id' },
    { header: 'Driver ID', field: 'user.id' },
    { header: 'Driver Name', field: 'user.name' },
    { header: 'Driver Contact', field: 'user.phone' },
    { header: 'Test Results mg/l', field: 'data' },
    // ADM-05。測定日時は日本時間で出す（UTC の ISO 文字列のままだった）。
    { header: 'Date Time', field: 'testing_time', customExport: csvDateTime },
    // EV-116 段階3。測定の時刻の時点の端末の時間帯（無い記録は空欄）。
    ...deviceTimeZoneExportFields(),
  ];

  const exportFieldsForRecentSubmission = [
    { header: 'Driver ID', field: 'id' },
    { header: 'Driver Name', field: 'name' },
    { header: 'Driver Contact', field: 'phone' },
    // EV-141。運転者単位の行を保ち、測定値と同じ順序で元の測定IDを出す。
    {
      header: 'Test IDs',
      field: 'alcoholtests',
      customExport: (tests: any[]) =>
        Array.isArray(tests) ? tests.map((test) => test.id ?? '').join('; ') : '',
    },
    {
      header: 'Test Result',
      field: 'alcoholtests',
      customExport: (tests: any[]) => {
        if (!Array.isArray(tests)) return '';
        return tests.map((test) => `${test.data}`).join('; ');
      },
    },
    // RV-325（EV-116 段階3）。測定ごとの端末の時間帯を Test IDs と同じ順に束ねる（無い測定は空）。
    ...bundledDeviceTimeZoneExportFields('alcoholtests'),
  ];

  // ADM-11-AC2。出力した条件をファイル名に入れる（表示中のページを出す）。
  const exportConditions: CsvCondition[] = [
    ['date', dateValue],
    ['tab', activetab],
    ['search', searchValue],
    ['sort', sortValue],
    ['page', currentPage],
  ];

  const selectedExportFields =
    activetab === 'recentsubmissions'
      ? exportFieldsForRecentSubmission
      : exportFields;

  return (
    // EV-309。1024px 未満では、ツールバーの右の組を次の行へ折り返し、右に寄せる。1024px 以上は1行のまま（検索の欄が縮む）。
    // 追加・エクスポートのボタンの文字は折らない（reusable/addButton・export）。
    // EV-339。1024px 以上は右の組を縮めない（lg:shrink-0。機器・車両のツールバーと同じ）。以前は右の組が中身より狭く縮み、
    // 「エクスポート」がカードの右の端から出ていた。
    <section className="flex flex-row max-lg:flex-wrap gap-y-2 justify-between w-full">
      <div className="flex w-2/5 justify-start">
        <Search
          placeholder={t('table.search')}
          onSearchChange={handleSearchChange}
        />
      </div>
      <div className="ml-auto flex flex-row lg:shrink-0 space-x-2 items-center">
        <SortBy
          options={sortingOptions}
          defaultValue={sortValue}
          onChange={handleSortChange}
        />
        <DateFilter defaultDate={dateValue} onDateChange={handleDateChange} />
        <Export
          data={DataForExport}
          exportFields={selectedExportFields}
          name="alcohol_tests"
          conditions={exportConditions}
        />
      </div>
    </section>
  );
};

export default Toolbar;
